import { api } from './client';
import { i18nMetaMap } from '@/lib/i18n-map';

/**
 * Cererile pacienților noi (docs/CERERI-PACIENTI.md) — suplimentul
 * `patientInquiries`. Tipurile oglindesc `InquiryView` / `InquiryDetail` din
 * `apps/api/src/modules/inquiries/inquiries.service.ts`.
 */

export const INQUIRY_STATUSES = ['new', 'contacted', 'booked', 'lost'] as const;
export type InquiryStatus = (typeof INQUIRY_STATUSES)[number];
export type InquiryFilter = 'open' | 'all' | InquiryStatus;
export type InquiryChannel = 'form' | 'manual' | 'telegram';

export const LOST_REASONS = [
  'no_answer',
  'price',
  'other_clinic',
  'not_needed',
  'spam',
  'other',
] as const;
export type LostReason = (typeof LOST_REASONS)[number];

export const INQUIRY_STATUS_META = i18nMetaMap<
  InquiryStatus,
  { label: string; tone: 'neutral' | 'ok' | 'warn' | 'danger' | 'info' }
>('inquiries', {
  new: { label: 'statusNew', tone: 'warn' },
  contacted: { label: 'statusContacted', tone: 'info' },
  booked: { label: 'statusBooked', tone: 'ok' },
  lost: { label: 'statusLost', tone: 'neutral' },
});

export const INQUIRY_CHANNEL_META = i18nMetaMap<InquiryChannel, { label: string }>('inquiries', {
  form: { label: 'channelForm' },
  manual: { label: 'channelManual' },
  telegram: { label: 'channelTelegram' },
});

export const LOST_REASON_META = i18nMetaMap<LostReason, { label: string }>('inquiries', {
  no_answer: { label: 'lostNoAnswer' },
  price: { label: 'lostPrice' },
  other_clinic: { label: 'lostOtherClinic' },
  not_needed: { label: 'lostNotNeeded' },
  spam: { label: 'lostSpam' },
  other: { label: 'lostOther' },
});

export interface Inquiry {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  message: string | null;
  preferredTime: string | null;
  channel: InquiryChannel;
  source: string | null;
  pageUrl: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  status: InquiryStatus;
  lostReason: LostReason | null;
  lostNote: string | null;
  assignedTo: string | null;
  assignedName: string | null;
  firstResponseAt: string | null;
  patientId: string | null;
  appointmentId: string | null;
  telegram: boolean;
  telegramUsername: string | null;
  createdByName: string | null;
  createdAt: string;
  lastActivityAt: string;
  closedAt: string | null;
  anonymised: boolean;
  waitingMinutes: number | null;
}

export type InquiryEventKind =
  | 'created'
  | 'message_in'
  | 'message_out'
  | 'note'
  | 'call'
  | 'status'
  | 'assigned'
  | 'booked'
  | 'phone';

export interface InquiryEvent {
  id: string;
  kind: InquiryEventKind;
  text: string | null;
  meta: Record<string, unknown>;
  delivery: 'sent' | 'failed' | 'skipped' | null;
  actorName: string | null;
  createdAt: string;
}

export interface InquiryDetail extends Inquiry {
  consentAt: string | null;
  consentText: string | null;
  events: InquiryEvent[];
  match: { id: string; name: string; phone: string | null; others: number } | null;
  patientName: string | null;
  appointment: {
    id: string;
    apptDate: string;
    hour: number;
    min: number;
    doctorName: string | null;
    status: string;
  } | null;
}

export interface InquirySummary {
  new: number;
  contacted: number;
  booked: number;
  lost: number;
  waiting: number;
  overdue: number;
  targetMinutes: number;
}

export interface InquiryStats {
  month: string;
  total: number;
  booked: number;
  bookedPct: number;
  medianResponseMinutes: number | null;
  byChannel: Array<{ channel: InquiryChannel; total: number; booked: number }>;
  bySource: Array<{ source: string | null; total: number; booked: number }>;
}

/** Scris de mână, nu derivat: `forbidNonWhitelisted` refuză orice câmp în plus. */
export interface CreateInquiryInput {
  name: string;
  phone?: string | null;
  email?: string | null;
  message?: string | null;
  preferredTime?: string | null;
  source?: string | null;
  assignedTo?: string | null;
}

export type UpdateInquiryInput = Partial<CreateInquiryInput>;

export interface InquiryStatusInput {
  status: 'new' | 'contacted' | 'lost';
  lostReason?: LostReason;
  lostNote?: string | null;
}

export interface InquirySettings {
  formEnabled: boolean;
  formSource: string | null;
  thankYouText: string | null;
  privacyUrl: string | null;
  allowedOrigins: string[];
  responseTargetMinutes: number;
  telegramGreeting: string | null;
  telegramSource: string | null;
  formUrl: string;
  formApiUrl: string;
  jsonUrl: string;
  telegram: {
    connected: boolean;
    username: string | null;
    tokenHint: string | null;
    connectedAt: string | null;
    lastError: string | null;
    webhookUrl: string;
    allowed: boolean;
  };
}

export interface InquirySettingsInput {
  formEnabled?: boolean;
  formSource?: string | null;
  thankYouText?: string | null;
  privacyUrl?: string | null;
  allowedOrigins?: string[];
  responseTargetMinutes?: number;
  telegramGreeting?: string | null;
  telegramSource?: string | null;
}

export const inquiriesApi = {
  list: (query: { status?: InquiryFilter; search?: string; limit?: number; offset?: number }) =>
    api.get<Inquiry[]>('/inquiries', {
      query: { ...query, search: query.search?.trim() || undefined },
    }),
  summary: () => api.get<InquirySummary>('/inquiries/summary'),
  stats: (month?: string) => api.get<InquiryStats>('/inquiries/stats', { query: { month } }),
  get: (id: string) => api.get<InquiryDetail>(`/inquiries/${id}`),
  create: (body: CreateInquiryInput) => api.post<InquiryDetail>('/inquiries', body),
  update: (id: string, body: UpdateInquiryInput) =>
    api.patch<InquiryDetail>(`/inquiries/${id}`, body),
  setStatus: (id: string, body: InquiryStatusInput) =>
    api.post<InquiryDetail>(`/inquiries/${id}/status`, body),
  addNote: (id: string, body: { kind: 'note' | 'call'; text?: string | null }) =>
    api.post<InquiryDetail>(`/inquiries/${id}/notes`, body),
  reply: (id: string, text: string) => api.post<InquiryDetail>(`/inquiries/${id}/reply`, { text }),
  remove: (id: string, reason?: string) =>
    api.delete<void>(`/inquiries/${id}`, { query: { reason: reason || undefined } }),

  settings: () => api.get<InquirySettings>('/inquiries/settings'),
  saveSettings: (body: InquirySettingsInput) =>
    api.put<InquirySettings>('/inquiries/settings', body),
  connectTelegram: (token: string) =>
    api.put<InquirySettings>('/inquiries/settings/telegram', { token }),
  disconnectTelegram: () => api.delete<InquirySettings>('/inquiries/settings/telegram'),
};
